import React from "react";
import { Box, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { tokens, displayFont } from "../theme";

/**
 * App-level error boundary. Without this, a render error anywhere unmounts the
 * whole tree to a blank white screen. This catches it and shows a recoverable
 * fallback instead.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // surface for debugging; could be wired to a logging service later
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          p: "2rem",
          textAlign: "center",
          background: tokens.surface,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: "3rem", color: tokens.danger }} />
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>
          Something went wrong
        </Box>
        <Box sx={{ fontSize: "0.9375rem", color: tokens.textMuted, maxWidth: "28rem" }}>
          An unexpected error occurred. Reloading usually fixes it. If it keeps happening,
          please let your administrator know.
        </Box>
        <Button
          onClick={this.handleReload}
          disableElevation
          variant="contained"
          sx={{ mt: "0.5rem", textTransform: "none", borderRadius: "8px", background: tokens.primary, "&:hover": { background: tokens.primaryHover }, px: "1.5rem", height: "2.75rem" }}
        >
          Reload
        </Button>
      </Box>
    );
  }
}
